import React, { useEffect, useState } from "react";
import axios from "axios";
import { Activity, TrendingUp, TrendingDown } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmt = (v, d = 2) =>
  v == null || Number.isNaN(Number(v))
    ? "—"
    : Number(v).toLocaleString("en-IN", { maximumFractionDigits: d });

export const SectorRotation = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const r = await axios.get(`${API}/regime`);
        setData(r.data);
      } catch {
        /* ignore */
      } finally {
        setLoading(false);
      }
    };
    load();
    const id = setInterval(load, 60_000);
    return () => clearInterval(id);
  }, []);

  const sectors = data?.sectors || [];
  const vix = data?.vix;
  const breadth = data?.breadth_ratio;

  return (
    <section
      data-testid="sector-rotation-widget"
      className="border border-grid p-4"
    >
      <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Activity size={14} style={{ color: "var(--bull)" }} />
          <div className="text-[10px] uppercase tracking-[0.3em] text-muted-3 font-mono-data">
            Regime · Sector Rotation
          </div>
        </div>
        <div className="flex items-center gap-4 font-mono-data text-xs">
          {vix != null && (
            <div data-testid="regime-vix">
              <span className="text-muted-3 uppercase tracking-wider text-[10px] mr-2">
                India VIX
              </span>
              <span
                className="font-semibold"
                style={{ color: vix > 20 ? "var(--bear)" : vix < 14 ? "var(--bull)" : "var(--text-primary)" }}
              >
                {fmt(vix, 2)}
              </span>
            </div>
          )}
          {breadth != null && (
            <div data-testid="regime-breadth">
              <span className="text-muted-3 uppercase tracking-wider text-[10px] mr-2">
                Breadth
              </span>
              <span
                className="font-semibold"
                style={{ color: breadth >= 1 ? "var(--bull)" : "var(--bear)" }}
              >
                {fmt(breadth, 2)}
              </span>
            </div>
          )}
        </div>
      </div>

      {loading ? (
        <div className="h-8 animate-pulse bg-white/[0.02]" />
      ) : sectors.length === 0 ? (
        <div className="text-[10px] text-muted-3 font-mono-data italic">
          Sector quotes unavailable — Upstox may be disconnected or the market is closed.
        </div>
      ) : (
        <div className="flex flex-wrap gap-2" data-testid="sector-chips">
          {sectors.map((s) => {
            const above = s.above_vwap;
            const color =
              above === true
                ? "var(--bull)"
                : above === false
                ? "var(--bear)"
                : "var(--text-muted)";
            const Icon = above === true ? TrendingUp : TrendingDown;
            return (
              <div
                key={s.name}
                data-testid={`sector-chip-${s.name.toLowerCase()}`}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 border font-mono-data text-[11px]"
                style={{ borderColor: color, color }}
                title={
                  s.vwap
                    ? `LTP ₹${fmt(s.ltp)} · VWAP ₹${fmt(s.vwap)}`
                    : `LTP ₹${fmt(s.ltp)}`
                }
              >
                {above !== null && <Icon size={11} />}
                <span className="uppercase tracking-wider">{s.name}</span>
                {s.change_pct != null && (
                  <span className="opacity-80">
                    {s.change_pct >= 0 ? "+" : ""}
                    {fmt(s.change_pct, 2)}%
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
};
